import { useState, useEffect } from "react";
import { logger } from "@/utils/logger";
import { motion } from "framer-motion";
import { Home, RotateCcw, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import { savePlayerNames, loadPlayerNames } from "@/utils/playerStorage";
import { recordGameWon } from "@/utils/playerProfile";

import { getVoiceLang } from "@/utils/voicePref";
const PUZZLES = [
  { id: 1, name: "Kucing", emoji: "🐱", pieces: 4, grid: 2 },
  { id: 2, name: "Bunga", emoji: "🌸", pieces: 4, grid: 2 },
  { id: 3, name: "Mobil", emoji: "🚗", pieces: 6, grid: 3 },
  { id: 4, name: "Rumah", emoji: "🏠", pieces: 6, grid: 3 },
  { id: 5, name: "Pelangi", emoji: "🌈", pieces: 9, grid: 3 },
];

const PIECE_EMOJIS = {
  "🐱": ["😺", "🐾", "🧶", "🐈", "😸", "🐱", "😻", "🙀", "😹"],
  "🌸": ["🌷", "🌻", "🌹", "🌺", "💐", "🌼", "🪷", "🌸", "🏵️"],
  "🚗": ["🚙", "🛞", "🔑", "⛽", "🚘", "🏎️", "🚗", "🛣️", "🚦"],
  "🏠": ["🏡", "🚪", "🪟", "🏠", "🔑", "💡", "🏘️", "🛋️", "🏗️"],
  "🌈": ["🔴", "🟠", "🟡", "🟢", "🔵", "🟣", "🌈", "☀️", "☁️"],
};

const speakText = (t) => {
  try { if (!window.speechSynthesis) return; window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(t); u.lang = getVoiceLang(); u.rate=0.85; u.pitch=1.0; window.speechSynthesis.speak(u);
  } catch (err) { logger.debug("game op failed", err); }
};

const PuzzlePotong = ({ onBackToMenu }) => {
  const { width: winW, height: winH } = useWindowSize();
  const [gameState, setGameState] = useState("menu");
  const [playerNames, setPlayerNames] = useState(() => { const s = loadPlayerNames(); return [s[0]||"",s[1]||""]; });
  const [currentLevel, setCurrentLevel] = useState(0);
  const [pieces, setPieces] = useState([]);
  const [placed, setPlaced] = useState([]);
  const [currentPlayer, setCurrentPlayer] = useState(0);
  const [selectedPiece, setSelectedPiece] = useState(null);
  const [soundOn, setSoundOn] = useState(true);
  const [moves, setMoves] = useState(0);

  const puzzle = PUZZLES[currentLevel];
  const p1 = playerNames[0] || "Pemain 1";
  const p2 = playerNames[1] || "Pemain 2";

  const startLevel = (lvl) => {
    savePlayerNames(playerNames);
    setCurrentLevel(lvl);
    const p = PUZZLES[lvl];
    const emojis = PIECE_EMOJIS[p.emoji].slice(0, p.pieces);
    const shuffled = [...emojis].sort(() => Math.random() - 0.5);
    setPieces(shuffled);
    setPlaced(Array(p.pieces).fill(null));
    setCurrentPlayer(0);
    setSelectedPiece(null);
    setMoves(0);
    setGameState("playing");
    if (soundOn) speakText(`Solve the puzzle ${p.name}! ${p.pieces} potongan!`);
  };

  const handlePieceClick = (idx) => {
    if (gameState !== "playing") return;
    // P1 picks piece
    if (currentPlayer === 0) {
      if (placed.includes(pieces[idx])) return;
      setSelectedPiece(idx);
      setCurrentPlayer(1);
      if (soundOn) speakText(`${p1} choose. ${p2} place in position!`);
      return;
    }
  };

  const handleSlotClick = (slotIdx) => {
    if (gameState !== "playing" || currentPlayer !== 1 || selectedPiece === null) return;
    if (placed[slotIdx] !== null) return;

    const newPlaced = [...placed];
    newPlaced[slotIdx] = pieces[selectedPiece];
    setPlaced(newPlaced);
    setSelectedPiece(null);
    setCurrentPlayer(0);
    setMoves(prev => prev + 1);

    // Check if correct position
    const correctEmojis = PIECE_EMOJIS[puzzle.emoji].slice(0, puzzle.pieces);
    if (newPlaced[slotIdx] === correctEmojis[slotIdx]) {
      if (soundOn) speakText("Correct!");
    }

    // Check win
    const allPlaced = newPlaced.every(p => p !== null);
    if (allPlaced) {
      setGameState("won");
      recordGameWon("puzzle");
      if (soundOn) speakText(`Puzzle ${puzzle.name} complete! ${p1} and ${p2} are great!`);
    }
  };

  if (gameState === "menu") {
    return (
      <div className="flex flex-col items-center w-full max-w-2xl mx-auto px-4 pt-4" data-testid="puzzle-potong-menu">
        <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="glass-card-strong rounded-2xl p-6 w-full text-center mb-4">
          <motion.span className="text-6xl block mb-3" animate={{ rotate: [0, 5, -5, 0] }} transition={{ duration: 3, repeat: Infinity }}>🧩</motion.span>
          <h1 className="font-fredoka text-3xl text-gold font-bold mb-2">Puzzle Potong</h1>
          <p className="font-fredoka text-gold-light/80 text-sm mb-4">Susun potongan gambar bersama!</p>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <input data-testid="puzzle-p1" type="text" value={playerNames[0]} onChange={e => setPlayerNames([e.target.value, playerNames[1]])} placeholder="Pemain 1" maxLength={15} className="glass-card border-gold/20 rounded-xl px-3 py-2 text-white font-fredoka text-sm focus:outline-none" />
            <input data-testid="puzzle-p2" type="text" value={playerNames[1]} onChange={e => setPlayerNames([playerNames[0], e.target.value])} placeholder="Pemain 2" maxLength={15} className="glass-card border-gold/20 rounded-xl px-3 py-2 text-white font-fredoka text-sm focus:outline-none" />
          </div>
        </motion.div>

        <div className="grid grid-cols-1 gap-2 w-full mb-4">
          {PUZZLES.map((p, idx) => (
            <Button key={p.id} onClick={() => startLevel(idx)} data-testid={`puzzle-level-${p.id}`}
              className="w-full h-14 font-fredoka font-bold text-base rounded-xl justify-between px-4"
              style={{ background: `linear-gradient(135deg, hsl(${idx * 50 + 180}, 60%, 50%), hsl(${idx * 50 + 180}, 60%, 40%))` }}>
              <span className="flex items-center gap-2"><span className="text-xl">{p.emoji}</span>Level {p.id} - {p.name}</span>
              <span className="text-xs opacity-80">{p.pieces} potong</span>
            </Button>
          ))}
        </div>
        <Button onClick={onBackToMenu} variant="outline" className="w-full glass-card border-gold/20 text-gold hover:bg-gold/10 font-fredoka rounded-xl"><Home className="w-4 h-4 mr-2" />Menu</Button>
      </div>
    );
  }

  if (gameState === "won") {
    const isLast = currentLevel >= PUZZLES.length - 1;
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] px-4" data-testid="puzzle-potong-win">
        <Confetti width={winW} height={winH} recycle={false} numberOfPieces={250} />
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="glass-card-strong rounded-2xl p-8 text-center max-w-md w-full">
          <span className="text-7xl block mb-3">{puzzle.emoji}</span>
          <h2 className="font-fredoka text-3xl text-gold font-bold mb-2">Puzzle Selesai!</h2>
          <p className="font-fredoka text-gold-light/80 mb-3">{p1} & {p2} hebat! {moves} langkah</p>
          <div className="flex flex-col gap-2">
            {!isLast && <Button onClick={() => startLevel(currentLevel + 1)} className="w-full font-fredoka font-bold rounded-xl h-10 bg-gradient-to-r from-green-400 to-emerald-500" data-testid="puzzle-next">Puzzle Berikutnya</Button>}
            <Button onClick={() => startLevel(currentLevel)} className="w-full font-fredoka font-bold rounded-xl h-10 bg-gradient-to-r from-yellow-400 to-amber-500"><RotateCcw className="w-4 h-4 mr-2" />Ulangi</Button>
            <Button onClick={() => setGameState("menu")} variant="outline" className="w-full glass-card border-gold/20 text-gold font-fredoka rounded-xl">Menu</Button>
          </div>
        </motion.div>
      </div>
    );
  }

  const grid = puzzle.grid;

  return (
    <div className="flex flex-col items-center w-full max-w-2xl mx-auto px-2" data-testid="puzzle-potong-game">
      <div className="w-full glass-card-strong rounded-xl p-2 mb-2 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <span className="font-fredoka text-gold font-bold text-sm">🧩 {puzzle.name}</span>
          <span className="font-fredoka text-gold-light/60 text-xs">Langkah: {moves}</span>
        </div>
        <div className="flex gap-1.5">
          <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 w-7 p-0">{soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}</Button>
          <Button onClick={() => setGameState("menu")} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 px-2"><Home className="w-3 h-3" /></Button>
        </div>
      </div>

      <div className="font-fredoka text-sm text-center mb-2">
        <span className={currentPlayer === 0 ? "text-cyan-400 font-bold" : "text-pink-400 font-bold"}>
          {currentPlayer === 0 ? `${p1}: Pilih potongan!` : `${p2}: Taruh di posisi!`}
        </span>
      </div>

      {/* Target image */}
      <div className="mb-3 text-center">
        <span className="text-5xl">{puzzle.emoji}</span>
        <p className="font-fredoka text-gold-light/60 text-xs mt-1">Target: {puzzle.name}</p>
      </div>

      {/* Puzzle board (slots) */}
      <div className="grid gap-2 mb-4 mx-auto" style={{ gridTemplateColumns: `repeat(${grid}, 1fr)`, maxWidth: grid * 80 }}>
        {Array.from({ length: puzzle.pieces }).map((_, i) => (
          <motion.button key={`slot-${i}`} onClick={() => handleSlotClick(i)}
            className={`w-16 h-16 sm:w-20 sm:h-20 rounded-xl flex items-center justify-center text-3xl sm:text-4xl border-2 ${
              placed[i] ? 'border-green-400/50 bg-green-900/20' : 'border-dashed border-gold/30 bg-white/5 hover:bg-white/10'
            }`}
            whileHover={!placed[i] ? { scale: 1.05 } : {}}
            whileTap={!placed[i] ? { scale: 0.9 } : {}}>
            {placed[i] || <span className="text-gold-light/30 text-lg">{i + 1}</span>}
          </motion.button>
        ))}
      </div>

      {/* Available pieces */}
      <p className="font-fredoka text-gold-light/60 text-xs mb-2">Potongan tersedia:</p>
      <div className="flex flex-wrap gap-2 justify-center">
        {pieces.map((piece, i) => {
          const isPlaced = placed.includes(piece);
          return (
            <motion.button key={`piece-${i}`} onClick={() => handlePieceClick(i)}
              disabled={isPlaced}
              className={`w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center text-2xl sm:text-3xl
                ${isPlaced ? 'opacity-30 bg-gray-700' : selectedPiece === i ? 'ring-4 ring-cyan-400 bg-cyan-900/30' : 'ingredient-3d'}
              `}
              whileHover={!isPlaced ? { scale: 1.1 } : {}}
              whileTap={!isPlaced ? { scale: 0.85 } : {}}>
              {piece}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};

export default PuzzlePotong;
